import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Box,
  Typography,
  Chip,
  makeStyles,
} from '@material-ui/core';
import { FactorMeta } from '../../types';

const useStyles = makeStyles(theme => ({
  container: {
    borderRadius: 12,
  },
  headerCell: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  factorChip: {
    textTransform: 'capitalize',
    fontWeight: 500,
  },
  progressRoot: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
  },
  progressBar: {
    borderRadius: 4,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: "#9084d838",
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

export const CompanyFactorContributionTable: React.FC<{
  contributions: Record<string, number>;
  factors: Record<string, FactorMeta>;
}> = ({ contributions, factors }) => {
  const classes = useStyles();
const safeContributions = contributions ?? {};
const safeFactors = factors ?? {};
  return (
    <Paper elevation={1} className={classes.container}>
      <Box px={2} py={1.5}>
        <Typography variant="h6">Factor Contributions</Typography>
        <Typography variant="body2" color="textSecondary">
          Contribution of each factor toward the overall reputation score
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Factor</TableCell>
              <TableCell className={classes.headerCell}>Value</TableCell>
              <TableCell className={classes.headerCell}>Max</TableCell>
              <TableCell className={classes.headerCell}>Impact</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {Object.keys(safeContributions).map(key => {
              const value = safeContributions[key];
              const max = safeFactors[key]?.max_value ?? 1;
              const progress = Math.min((value / max) * 100, 100);

              return (
                <TableRow key={key} className={classes.row}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={key.replace(/_/g, ' ')}
                      className={classes.factorChip}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{value}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {max}
                    </Typography>
                  </TableCell>

                  <TableCell width="40%">
                    <Box display="flex" alignItems="center">
                      <Box flexGrow={1} mr={1}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          classes={{
                            root: classes.progressRoot,
                            bar: classes.progressBar,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {Math.round(progress)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
